import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'

// ─── Types ────────────────────────────────────────────────────

export interface NoizaGmv {
  total_gmv: number
  total_orders: number
  avg_ticket: number
}

export interface NoizaGmvDaily {
  day: string
  gmv: number
  orders: number
}

export interface NoizaGmvByRestaurant {
  restaurant_id: string
  restaurant_name: string
  slug: string
  gmv: number
  orders: number
  avg_ticket: number
}

export interface NoizaChannelDistribution {
  channel: string
  orders: number
  pct: number
}

export interface NoizaSelfServicePct {
  self_service_pct: number
  waiter_pct: number
}

export interface NoizaRepeatCustomers {
  repeat_count: number
  repeat_pct: number
}

export interface NoizaTopProduct {
  product_name: string
  restaurant_name: string
  restaurant_id?: string
  quantity: number
  revenue: number
}

export interface NoizaLowDay {
  restaurant_id: string
  restaurant_name: string
  day_of_week: string | number
  avg_gmv: number
  avg_orders: number
}

export interface NoizaLowHour {
  restaurant_id: string
  restaurant_name: string
  hour_of_day: string | number
  avg_gmv: number
  avg_orders: number
}

export interface NoizaSalesDaily {
  restaurant_id: string
  restaurant_name: string
  day: string
  gmv: number
  orders: number
}

export interface NoizaSalesHourly {
  restaurant_id: string
  restaurant_name: string
  hour: string | number
  gmv: number
  orders: number
}

export interface NoizaOrderValid {
  id: string
  created_at: string
  total: number
  status: string
  restaurant_id: string
  restaurant_name?: string
  customer_name?: string
}

export interface NoizaMetricsData {
  gmv: NoizaGmv
  gmvDaily: NoizaGmvDaily[]
  gmvByRestaurant: NoizaGmvByRestaurant[]
  channelDistribution: NoizaChannelDistribution[]
  selfServicePct: NoizaSelfServicePct
  activeRestaurants: number
  uniqueCustomers: number
  repeatCustomers: NoizaRepeatCustomers
  topProducts: NoizaTopProduct[]
  lowDays: NoizaLowDay[]
  lowHours: NoizaLowHour[]
  salesDaily: NoizaSalesDaily[]
  salesHourly: NoizaSalesHourly[]
  recentOrders: NoizaOrderValid[]
}

const DEFAULT_DATA: NoizaMetricsData = {
  gmv: { total_gmv: 0, total_orders: 0, avg_ticket: 0 },
  gmvDaily: [],
  gmvByRestaurant: [],
  channelDistribution: [],
  selfServicePct: { self_service_pct: 0, waiter_pct: 0 },
  activeRestaurants: 0,
  uniqueCustomers: 0,
  repeatCustomers: { repeat_count: 0, repeat_pct: 0 },
  topProducts: [],
  lowDays: [],
  lowHours: [],
  salesDaily: [],
  salesHourly: [],
  recentOrders: [],
}

export interface RestaurantOption {
  id: string
  name: string
  slug: string
}

export interface DateRange {
  from: string
  to: string
}

// Status values that should be EXCLUDED from metrics (cancelled / refunded)
const EXCLUDED_STATUSES = new Set([
  'cancelled',
  'canceled',
  'cancelado',
  'refunded',
  'reembolsado',
  'voided',
])

// ─── Main Hook ─────────────────────────────────────────────

export function useNoizaMetrics(restaurantId?: string, dateRange?: DateRange) {
  const [data, setData] = useState<NoizaMetricsData>(DEFAULT_DATA)
  const [restaurants, setRestaurants] = useState<RestaurantOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch restaurants list once
  useEffect(() => {
    async function fetchRestaurants() {
      try {
        const { data, error } = await supabase
          .from('restaurants')
          .select('id, name, slug')
          .eq('is_active', true)
          .order('name')
        if (!error && data) {
          setRestaurants(data)
        }
      } catch (err) {
        console.error('Error fetching restaurants:', err)
      }
    }
    fetchRestaurants()
  }, [])

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const filterById = !!(restaurantId && restaurantId !== 'all')
      const hasDateFilter = !!(dateRange && dateRange.from && dateRange.to)

      // ─── 1. Fetch orders directly with server-side date filter ────
      // We query orders directly so EVERY metric respects the date filter.
      // Costa Rica is UTC-6. Interpret the user-picked dates as CR-local time
      // so "Hoy" maps to the right 24-hour window (not UTC midnight).
      const CR_TZ_OFFSET = '-06:00'
      const fromIso = hasDateFilter
        ? `${dateRange!.from}T00:00:00.000${CR_TZ_OFFSET}`
        : null
      const toIso = hasDateFilter
        ? `${dateRange!.to}T23:59:59.999${CR_TZ_OFFSET}`
        : null

      let ordersQuery = supabase
        .from('orders')
        .select(
          'id, created_at, total, status, restaurant_id, order_channel, customer_name, customer_phone, table_id',
        )
        .order('created_at', { ascending: false })
        .limit(20000)

      if (hasDateFilter) {
        ordersQuery = ordersQuery
          .gte('created_at', fromIso!)
          .lte('created_at', toIso!)
      }
      if (filterById) {
        ordersQuery = ordersQuery.eq('restaurant_id', restaurantId)
      }

      const { data: ordersRaw, error: ordersErr } = await ordersQuery
      if (ordersErr) {
        console.warn('[NOIZA Metrics] orders query error:', ordersErr.message)
      }

      // Drop cancelled / refunded
      const validOrders = (ordersRaw || []).filter((o: any) => {
        const s = String(o.status || '')
          .toLowerCase()
          .trim()
        return !EXCLUDED_STATUSES.has(s)
      })

      // ─── 2. Fetch order_items for valid orders (chunked) ────────
      const orderIds = validOrders.map((o: any) => String(o.id))
      const itemsRaw: any[] = []
      if (orderIds.length > 0) {
        const CHUNK = 400
        for (let i = 0; i < orderIds.length; i += CHUNK) {
          const chunk = orderIds.slice(i, i + CHUNK)
          const { data: itemsChunk, error: itemsErr } = await supabase
            .from('order_items')
            .select('*')
            .in('order_id', chunk)
          if (itemsErr) {
            console.warn(
              '[NOIZA Metrics] order_items chunk error:',
              itemsErr.message,
            )
            continue
          }
          if (itemsChunk) itemsRaw.push(...itemsChunk)
        }
      }

      // Lookup maps
      const orderToRest: Record<string, string> = {}
      validOrders.forEach((o: any) => {
        orderToRest[String(o.id)] = String(o.restaurant_id)
      })
      const restNameById = new Map<string, string>()
      const restSlugById = new Map<string, string>()
      restaurants.forEach((r) => {
        restNameById.set(String(r.id), r.name)
        restSlugById.set(String(r.id), r.slug || '')
      })

      // ─── 3. GMV totals ─────────────────────────────────────────
      const totalGmv = validOrders.reduce(
        (sum: number, o: any) => sum + Number(o.total || 0),
        0,
      )
      const totalOrders = validOrders.length
      const avgTicket = totalOrders > 0 ? totalGmv / totalOrders : 0

      // ─── 4. GMV daily ──────────────────────────────────────────
      const gmvDailyMap: Record<
        string,
        { day: string; gmv: number; orders: number }
      > = {}
      validOrders.forEach((o: any) => {
        const day = String(o.created_at).substring(0, 10) // YYYY-MM-DD
        if (!gmvDailyMap[day]) gmvDailyMap[day] = { day, gmv: 0, orders: 0 }
        gmvDailyMap[day].gmv += Number(o.total || 0)
        gmvDailyMap[day].orders += 1
      })
      const gmvDaily = Object.values(gmvDailyMap).sort((a, b) =>
        a.day.localeCompare(b.day),
      )

      // ─── 5. GMV by restaurant ──────────────────────────────────
      const gmvByRestMap: Record<
        string,
        {
          restaurant_id: string
          restaurant_name: string
          slug: string
          gmv: number
          orders: number
        }
      > = {}
      validOrders.forEach((o: any) => {
        const rid = String(o.restaurant_id)
        if (!gmvByRestMap[rid]) {
          gmvByRestMap[rid] = {
            restaurant_id: rid,
            restaurant_name: restNameById.get(rid) || 'Sin nombre',
            slug: restSlugById.get(rid) || '',
            gmv: 0,
            orders: 0,
          }
        }
        gmvByRestMap[rid].gmv += Number(o.total || 0)
        gmvByRestMap[rid].orders += 1
      })
      const gmvByRestaurant = Object.values(gmvByRestMap)
        .map((r) => ({
          ...r,
          avg_ticket: r.orders > 0 ? r.gmv / r.orders : 0,
        }))
        .sort((a, b) => b.gmv - a.gmv)

      // ─── 6. Sales hourly (aggregated across all dates in range) ─
      const hourlyMap: Record<
        string,
        {
          hour: number
          restaurant_id: string
          restaurant_name: string
          gmv: number
          orders: number
        }
      > = {}
      validOrders.forEach((o: any) => {
        const d = new Date(o.created_at)
        if (isNaN(d.getTime())) return
        const hour = d.getHours()
        const key = String(hour)
        if (!hourlyMap[key]) {
          hourlyMap[key] = {
            hour,
            restaurant_id: '',
            restaurant_name: '',
            gmv: 0,
            orders: 0,
          }
        }
        hourlyMap[key].gmv += Number(o.total || 0)
        hourlyMap[key].orders += 1
      })
      const salesHourly = Object.values(hourlyMap).sort(
        (a, b) => Number(a.hour) - Number(b.hour),
      )

      // ─── 7. Sales daily (same shape as gmvDaily for chart compat) ─
      const salesDaily = gmvDaily.map((d) => ({
        ...d,
        restaurant_id: '',
        restaurant_name: '',
      }))

      // ─── 8. Channel distribution ───────────────────────────────
      const channelMap: Record<string, number> = {}
      validOrders.forEach((o: any) => {
        const ch = String(o.order_channel || 'other').trim() || 'other'
        channelMap[ch] = (channelMap[ch] || 0) + 1
      })
      const channelDistribution = Object.entries(channelMap)
        .map(([channel, orders]) => ({
          channel,
          orders,
          pct: totalOrders > 0 ? (orders / totalOrders) * 100 : 0,
        }))
        .sort((a, b) => b.orders - a.orders)

      // ─── 9. Top products from order_items ──────────────────────
      const productMap: Record<
        string,
        {
          product_name: string
          restaurant_id: string
          restaurant_name: string
          quantity: number
          revenue: number
        }
      > = {}
      itemsRaw.forEach((item: any) => {
        const name = item.item_name_snapshot || 'Producto'
        const oid = String(item.order_id || '')
        const rid = orderToRest[oid] || ''
        const rname = restNameById.get(rid) || ''
        // Quantity: fallback to 1 if null/0 (matches working metrics-hooks.ts pattern)
        const qty = Number(item.quantity) || 1
        // Revenue: prefer the pre-computed line_total column.
        // Fall back to qty * unit_price_snapshot if line_total is missing.
        const lineTotal = Number(item.line_total)
        const unitPrice = Number(item.unit_price_snapshot || 0)
        const revenue =
          Number.isFinite(lineTotal) && lineTotal > 0
            ? lineTotal
            : qty * unitPrice
        const key = `${rid}__${name}`
        if (!productMap[key]) {
          productMap[key] = {
            product_name: name,
            restaurant_id: rid,
            restaurant_name: rname,
            quantity: 0,
            revenue: 0,
          }
        }
        productMap[key].quantity += qty
        productMap[key].revenue += revenue
      })
      const topProducts = Object.values(productMap).sort(
        (a, b) => b.revenue - a.revenue,
      )

      // ─── 10. Unique customers ──────────────────────────────────
      const customerKeys = new Set<string>()
      validOrders.forEach((o: any) => {
        const k = o.customer_phone || o.customer_name
        if (k) customerKeys.add(String(k).trim().toLowerCase())
      })
      const uniqueCustomers = customerKeys.size

      // ─── 11. Recent orders ─────────────────────────────────────
      const recentOrders = validOrders.slice(0, 50).map((o: any) => ({
        id: String(o.id),
        created_at: o.created_at,
        total: Number(o.total || 0),
        status: o.status || '',
        restaurant_id: String(o.restaurant_id),
        restaurant_name: restNameById.get(String(o.restaurant_id)) || '',
        customer_name: o.customer_name || 'Cliente',
      }))

      // ─── 12. Self-service vs waiter pct (from order_channel) ───
      const WAITER_HINTS = ['waiter', 'mesero', 'pos', 'caja', 'cashier']
      const SELF_HINTS = ['qr', 'self', 'web', 'online', 'marketplace']
      let waiterCount = 0
      let selfCount = 0
      validOrders.forEach((o: any) => {
        const ch = String(o.order_channel || '').toLowerCase()
        if (WAITER_HINTS.some((w) => ch.includes(w))) waiterCount++
        else if (SELF_HINTS.some((s) => ch.includes(s))) selfCount++
      })
      const classifiedTotal = waiterCount + selfCount
      const selfServicePct: NoizaSelfServicePct = {
        self_service_pct:
          classifiedTotal > 0 ? (selfCount / classifiedTotal) * 100 : 0,
        waiter_pct:
          classifiedTotal > 0 ? (waiterCount / classifiedTotal) * 100 : 0,
      }

      // ─── 13. Repeat customers (≥2 orders in window) ────────────
      const phoneCounts: Record<string, number> = {}
      validOrders.forEach((o: any) => {
        const k = o.customer_phone || o.customer_name
        if (!k) return
        const key = String(k).trim().toLowerCase()
        phoneCounts[key] = (phoneCounts[key] || 0) + 1
      })
      const repeatCount = Object.values(phoneCounts).filter((c) => c > 1).length
      const totalDistinctCustomers = Object.keys(phoneCounts).length
      const repeatCustomers: NoizaRepeatCustomers = {
        repeat_count: repeatCount,
        repeat_pct:
          totalDistinctCustomers > 0
            ? (repeatCount / totalDistinctCustomers) * 100
            : 0,
      }

      // ─── 14. Active restaurants = distinct restaurant_ids in range ─
      const activeRestaurants = new Set(
        validOrders.map((o: any) => String(o.restaurant_id)),
      ).size

      setData({
        gmv: {
          total_gmv: totalGmv,
          total_orders: totalOrders,
          avg_ticket: avgTicket,
        },
        gmvDaily,
        gmvByRestaurant,
        channelDistribution,
        selfServicePct,
        activeRestaurants,
        uniqueCustomers,
        repeatCustomers,
        topProducts,
        lowDays: [],
        lowHours: [],
        salesDaily,
        salesHourly,
        recentOrders,
      })
    } catch (err: any) {
      console.error('[NOIZA Platform] Error fetching metrics:', err)
      setError(err?.message || 'Error loading platform metrics')
    } finally {
      setLoading(false)
    }
  }, [restaurantId, dateRange, restaurants])

  useEffect(() => {
    fetchAll()
    const interval = setInterval(fetchAll, 60000)
    return () => clearInterval(interval)
  }, [fetchAll])

  // Realtime subscription — refetch on any order change
  useEffect(() => {
    const filterById = restaurantId && restaurantId !== 'all'
    const channelName = `noiza-metrics-orders-${restaurantId || 'all'}`

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          ...(filterById ? { filter: `restaurant_id=eq.${restaurantId}` } : {}),
        },
        () => {
          console.log('[NOIZA Metrics] Order change detected — refetching')
          fetchAll()
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [restaurantId, fetchAll])

  return { data, loading, error, refetch: fetchAll, restaurants }
}
